import { Layout, Button } from "antd";
import { Header, Content } from "antd/es/layout/layout";
import Sider from "antd/es/layout/Sider";
import { useState, useEffect, useLayoutEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSecureService } from "../store/service";
import { useAtom } from "jotai";
import { userAtom } from "../store/atoms";
import styles from "./mainLayout.module.scss";
import AuthChecker from "../components/AuthChecker/AuthChecker";
import SidebarContent from "../components/Sidebar/SidebarContent";
import MobileDrawer from "../components/MobileDrawer/MobileDrawer";

const getSavedSidebarCollapsed = (fallback: boolean) => {
  try {
    const saved = localStorage.getItem('sidebarCollapsed');
    if (saved !== null) {
      return JSON.parse(saved);
    }
  } catch {
    // ignore
  }
  return fallback;
};

const HeaderlessLayout = ({ children, title, icon, autoCollapse, forceInitialCollapse }: { children: React.ReactNode, title: string, icon: React.ReactNode, autoCollapse?: boolean, forceInitialCollapse?: boolean }) => {
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const { doLogout } = useSecureService();
  const navigate = useNavigate();
  const [user] = useAtom(userAtom);

  const location = useLocation();

  // Mobile drawer state — route değişince sıfırlanır; Drawer sadece mobilde mount
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 800
  );
  
  // Sidebar collapse state - forceInitialCollapse varsa başlangıçta kapalı yap, yoksa localStorage'dan al veya default değeri kullan
  const getInitialCollapsedState = () => {
    if (forceInitialCollapse) {
      return true;
    }
    return getSavedSidebarCollapsed(autoCollapse || false);
  };
  
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(getInitialCollapsedState);
  const [mainStageReady, setMainStageReady] = useState(false);

  useEffect(() => {
    let r2 = 0;
    const r1 = requestAnimationFrame(() => {
      r2 = requestAnimationFrame(() => {
        setMainStageReady(true);
      });
    });
    return () => {
      cancelAnimationFrame(r1);
      cancelAnimationFrame(r2);
    };
  }, []);

  // Sidebar collapse handler
  const handleSidebarCollapse = (collapsed: boolean) => {
    setIsSidebarCollapsed(collapsed);
    localStorage.setItem('sidebarCollapsed', JSON.stringify(collapsed));
    document.documentElement.classList.toggle('sidebar-collapsed', collapsed);
  };

  // forceInitialCollapse değiştiğinde sadece başlangıçta sidebar'ı kapalı yap
  useLayoutEffect(() => {
    if (forceInitialCollapse) {
      setIsSidebarCollapsed(true);
      localStorage.setItem('sidebarCollapsed', JSON.stringify(true));
      document.documentElement.classList.add('sidebar-collapsed');
    }
  }, [forceInitialCollapse]);

  useLayoutEffect(() => {
    document.documentElement.classList.toggle('sidebar-collapsed', isSidebarCollapsed);
  }, [isSidebarCollapsed, location.pathname]);

  useEffect(() => {
    const currentPath = location.pathname;
    const isSettings = currentPath === '/settings' || currentPath.startsWith('/settings/');
    const isDashboard = currentPath === '/dashboard' || currentPath === '/dashboard/';
    const isInternal = currentPath === '/internal-projects' || currentPath.startsWith('/internal-projects/');
    if (user?.role === 'Guest') {
      if (isSettings) {
        navigate('/external-projects');
        return;
      }
      if (isInternal) {
        navigate('/external-projects');
        return;
      }
    }
    if (!user && (isSettings || isDashboard)) {
      navigate('/internal-projects');
    }
    if (currentPath === '/') {
      if (!user) {
        navigate('/internal-projects');
      } else if (user.role === 'Guest') {
        navigate('/external-projects');
      } else {
        navigate('/dashboard');
      }
    }
  }, [user, location.pathname, navigate]);

  // Menu click handler
  const handleMenuClick = ({ key }: { key: string }) => {
    if (key === 'logout') {
      doLogout();
      navigate('/login');
    } else if (key === 'login') {
      navigate('/login');
    } else {
      setSelectedKeys([key]);
      
      // Mobile drawer'ı kapat
      if (isMobile) {
        setIsMobileDrawerOpen(false);
      }
      
      navigate(key);
    }
  };

  // Logout handler (reserved for future use in layout)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleLogout = async () => {
    await doLogout();
    navigate('/login');
  };

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 800);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setIsMobileDrawerOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    setIsMobileDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const currentPath = location.pathname;
    if (currentPath.startsWith('/internal-projects') || currentPath.startsWith('/projects') || currentPath.startsWith('/snps')) {
      setSelectedKeys(['/internal-projects']);
    } else if (currentPath.startsWith('/external-projects')) {
      setSelectedKeys(['/external-projects']);
    } else {
      setSelectedKeys([currentPath]);
    }
  }, [location.pathname]);



  return (
    <AuthChecker
      isMobile={isMobile}
      selectedKeys={selectedKeys}
      onMenuClick={handleMenuClick}
      onMobileDrawerOpen={() => setIsMobileDrawerOpen(true)}
    >
      <Layout
        data-app-layout-shell
        className={`${styles.layout} ${styles.ready} ${styles.mainStage} ${mainStageReady ? styles.mainStageReady : ''}`}
      >
        {/* Desktop Sider */}
        {!isMobile && (
          <Sider 
            width="10%" 
            collapsedWidth={68}
            collapsed={isSidebarCollapsed}
            className={styles.sider}
          >
            <SidebarContent 
              selectedKeys={selectedKeys}
              onMenuClick={({ key }) => {
                if (key === 'logout') {
                  doLogout();
                  navigate('/login');
                } else if (key === 'login') {
                  navigate('/login');
                } else {
                  navigate(key);
                }
              }}
              isCollapsed={isSidebarCollapsed}
              onToggleCollapse={() => handleSidebarCollapse(!isSidebarCollapsed)}
            />
          </Sider>
        )}
        
        <Layout className={`${styles.mainColumn} ${isSidebarCollapsed ? styles.layoutCollapsed : ''}`}>
        
            {/* Mobile Menu Button */}
            {isMobile && (
              <Header className={styles.header}>
              <Button 
                type="primary" 
                icon={<img src="/layout.svg" alt="menu" width={20} height={20} />}
                onClick={() => setIsMobileDrawerOpen(true)}
                className={styles.mobileMenuButton}
              />
              <div
                id="file-detail-mobile-header-slot"
                className={styles.fileDetailMobileHeaderSlot}
              />
            </Header>
          )}
          
          <Content className={styles.headerlessContent} data-app-scroll-lock>
            <div className={styles.headerlessContentContainer}>
              <div className={styles.headerlessContentBody}>{children}</div>
            </div>
          </Content>
        </Layout>
        
        {isMobile ? (
          <MobileDrawer
            isOpen={isMobileDrawerOpen}
            onClose={() => setIsMobileDrawerOpen(false)}
            selectedKeys={selectedKeys}
            onMenuClick={handleMenuClick}
          />
        ) : null}
      </Layout>
    </AuthChecker>
  );
};

export default HeaderlessLayout;
